<script>
import { mapState, mapActions } from 'vuex'
import { BModal, BSpinner } from 'bootstrap-vue-next'
import { addonsApi } from '@/helpers/addonsApi'
import { formatCurrency } from '@/utils/currencyFormatter'
import { translateServerMessage, translateApiError } from '@/utils/serverMessages'

const MAX_ADDON_QUANTITY = 20

export default {
  name: 'LimitPaywallModal',
  components: { BModal, BSpinner },
  data() {
    return {
      loading: false,
      catalogError: false,
      purchasingKey: null,
      purchaseError: null,
      purchaseSuccess: false,
      purchasedKind: null,
      catalog: [],
      canPurchase: false,
      reason: null,
      // Per-item quantity: one purchase = one prorated charge for the lot
      quantities: {},
      // Item awaiting immediate-charge confirmation (inline step, no nested modal)
      pendingItem: null
    }
  },
  computed: {
    ...mapState('paywall', ['visible', 'limitName', 'message', 'code', 'params', 'creditType']),
    /** Server 403s carry a code+params (serverMessages convention) - render
     *  them in the active locale; page-initiated opens pass a localized
     *  message directly and fall through unchanged. */
    displayMessage() {
      let params = this.params
      if (params && params.resource) {
        const resourceKey = `server.limits.${params.resource}`
        if (this.$te(resourceKey)) {
          params = { ...params, resource: this.$t(resourceKey) }
        } else {
          params = { ...params, resource: String(params.resource).replace(/^max_/, '').replace(/_/g, ' ') }
        }
      }
      return translateServerMessage({ code: this.code, params, message: this.message })
    },
    showModal: {
      get() {
        return this.visible
      },
      set(value) {
        if (!value) this.hide()
      }
    },
    /** Recurring add-ons that raise exactly the limit the user just hit */
    offerings() {
      return this.catalog.filter(
        (item) => item.kind === 'recurring_limit' && item.affected_limit_key === this.limitName
      )
    },
    /** One-time credit packs covering the consumable the user just ran out of */
    creditPackOfferings() {
      if (!this.creditType) return []
      return this.catalog.filter(
        (item) => item.kind === 'credit_pack' && item.credit_type === this.creditType
      )
    },
    hasOfferings() {
      return this.offerings.length > 0 || this.creditPackOfferings.length > 0
    }
  },
  watch: {
    visible(newValue) {
      if (newValue) {
        this.purchaseError = null
        this.purchaseSuccess = false
        this.purchasedKind = null
        this.quantities = {}
        this.pendingItem = null
        this.loadCatalog()
      }
    }
  },
  methods: {
    ...mapActions('paywall', ['hide']),
    async loadCatalog() {
      this.loading = true
      this.catalogError = false
      try {
        const data = await addonsApi.getAddons()
        this.catalog = data.catalog || []
        this.canPurchase = data.can_purchase
        this.reason = data.reason
      } catch (error) {
        console.error('Failed to load add-on catalog for paywall:', error)
        this.catalog = []
        this.catalogError = true
      } finally {
        this.loading = false
      }
    },
    buy(item) {
      this.purchaseError = null
      this.pendingItem = item
    },
    cancelPending() {
      this.pendingItem = null
    },
    async confirmPurchase() {
      const item = this.pendingItem
      if (!item) return
      this.purchaseError = null
      this.purchasingKey = item.key
      try {
        if (item.kind === 'credit_pack') {
          await addonsApi.purchaseCreditPack(item.key)
        } else {
          await addonsApi.purchaseAddon(item.key, this.qty(item))
        }
        this.purchasedKind = item.kind
        this.purchaseSuccess = true
        this.pendingItem = null
      } catch (error) {
        this.purchaseError = translateApiError(error)
      } finally {
        this.purchasingKey = null
      }
    },
    qty(item) {
      return this.quantities[item.key] || 1
    },
    bumpQty(item, delta) {
      const next = Math.min(MAX_ADDON_QUANTITY, Math.max(1, this.qty(item) + delta))
      this.quantities = { ...this.quantities, [item.key]: next }
    },
    maxQty() {
      return MAX_ADDON_QUANTITY
    },
    hasQuantity(item) {
      return item.kind === 'recurring_limit'
    },
    totalMonthly(item) {
      return (item.monthly_price_usd || 0) * this.qty(item)
    },
    goToPlans() {
      this.hide()
      this.$router.push('/app/subscription/plans')
    },
    itemName(item) {
      const translated = this.$t(item.name_i18n_key)
      return translated === item.name_i18n_key ? item.display_name : translated
    },
    formatPrice(amount) {
      return formatCurrency(amount, 'USD')
    }
  }
}
</script>

<template>
  <BModal
    v-model="showModal"
    :title="$t('addons.paywall.title')"
    hide-footer
    centered
  >
    <div v-if="purchaseSuccess" class="alert alert-success">
      <i class="mdi mdi-check-circle me-1"></i>
      {{ purchasedKind === 'credit_pack' ? $t('addons.paywall.credits_success') : $t('addons.paywall.success') }}
    </div>

    <template v-else>
      <p class="text-muted">{{ displayMessage }}</p>

      <div v-if="purchaseError" class="alert alert-danger alert-dismissible">
        {{ purchaseError }}
        <button type="button" class="btn-close" @click="purchaseError = null"></button>
      </div>

      <div v-if="loading" class="text-center my-4">
        <BSpinner small />
      </div>

      <template v-else-if="pendingItem">
        <!-- Immediate-charge confirmation step -->
        <div class="d-flex justify-content-between align-items-center border rounded p-3 mb-3">
          <div class="fw-semibold">
            {{ itemName(pendingItem) }}
            <span v-if="hasQuantity(pendingItem) && qty(pendingItem) > 1" class="text-muted">
              × {{ qty(pendingItem) }}
            </span>
          </div>
          <div class="fw-bold">
            <template v-if="pendingItem.kind === 'credit_pack'">
              {{ formatPrice(pendingItem.one_time_price_usd) }}
            </template>
            <template v-else>
              {{ formatPrice(totalMonthly(pendingItem)) }}{{ $t('addons.section.per_month') }}
            </template>
          </div>
        </div>

        <div class="alert alert-warning d-flex align-items-start">
          <i class="mdi mdi-credit-card-clock-outline me-2 fs-5"></i>
          <div>
            <strong>{{ $t('addons.purchase_confirm.immediate_charge') }}</strong>
            <div class="small mt-1">
              {{ pendingItem.kind === 'credit_pack'
                ? $t('addons.purchase_confirm.one_time_note', { price: formatPrice(pendingItem.one_time_price_usd) })
                : $t('addons.purchase_confirm.recurring_note', { price: formatPrice(totalMonthly(pendingItem)) }) }}
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-outline-secondary" :disabled="purchasingKey !== null" @click="cancelPending">
            {{ $t('addons.purchase_confirm.back') }}
          </button>
          <button type="button" class="btn btn-primary" :disabled="purchasingKey !== null" @click="confirmPurchase">
            <BSpinner v-if="purchasingKey !== null" small class="me-1" />
            <i v-else class="mdi mdi-credit-card-check-outline me-1"></i>
            {{ $t('addons.purchase_confirm.confirm') }}
          </button>
        </div>
      </template>

      <template v-else>
        <div v-if="hasOfferings && canPurchase">
          <template v-if="offerings.length > 0">
            <h6 class="mb-3">{{ $t('addons.paywall.buy_addon') }}</h6>
            <div
              v-for="item in offerings"
              :key="item.key"
              class="d-flex justify-content-between align-items-center flex-wrap gap-2 border rounded p-3 mb-2"
            >
              <div>
                <div class="fw-semibold">{{ itemName(item) }}</div>
                <small class="text-muted">
                  {{ $t('addons.paywall.monthly', { price: formatPrice(item.monthly_price_usd) }) }}
                </small>
                <div v-if="hasQuantity(item) && qty(item) > 1" class="fw-semibold small">
                  {{ $t('addons.qty.total', { price: formatPrice(totalMonthly(item)) }) }}
                </div>
              </div>
              <div class="d-flex align-items-center flex-wrap gap-2">
                <div v-if="hasQuantity(item)" class="input-group input-group-sm paywall-qty">
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    :disabled="qty(item) <= 1 || purchasingKey !== null"
                    :aria-label="$t('addons.qty.decrease')"
                    @click="bumpQty(item, -1)"
                  >
                    <i class="mdi mdi-minus"></i>
                  </button>
                  <span class="form-control text-center bg-white">{{ qty(item) }}</span>
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    :disabled="qty(item) >= maxQty() || purchasingKey !== null"
                    :aria-label="$t('addons.qty.increase')"
                    @click="bumpQty(item, 1)"
                  >
                    <i class="mdi mdi-plus"></i>
                  </button>
                </div>
                <button
                  class="btn btn-sm btn-primary text-nowrap"
                  :disabled="purchasingKey !== null"
                  @click="buy(item)"
                >
                  <BSpinner v-if="purchasingKey === item.key" small />
                  <span v-else>
                    {{ $t('addons.section.buy') }}
                    <template v-if="hasQuantity(item) && qty(item) > 1"> ({{ qty(item) }})</template>
                  </span>
                </button>
              </div>
            </div>
          </template>

          <template v-if="creditPackOfferings.length > 0">
            <h6 class="mb-3">{{ $t('addons.paywall.buy_credits') }}</h6>
            <div
              v-for="item in creditPackOfferings"
              :key="item.key"
              class="d-flex justify-content-between align-items-center border rounded p-3 mb-2"
            >
              <div>
                <div class="fw-semibold">{{ itemName(item) }}</div>
                <small class="text-muted">
                  {{ $t('addons.paywall.one_time', { price: formatPrice(item.one_time_price_usd) }) }}
                </small>
              </div>
              <button
                class="btn btn-sm btn-primary"
                :disabled="purchasingKey !== null"
                @click="buy(item)"
              >
                <BSpinner v-if="purchasingKey === item.key" small />
                <span v-else>{{ $t('addons.section.buy') }}</span>
              </button>
            </div>
          </template>

          <div class="text-center text-muted my-2">{{ $t('addons.paywall.or_upgrade') }}</div>
        </div>

        <div v-else-if="hasOfferings && !canPurchase" class="alert alert-info">
          {{ $t(`addons.reasons.${reason}`) }}
        </div>

        <div v-else-if="catalogError" class="alert alert-warning">
          {{ $t('addons.paywall.catalog_error') }}
        </div>

        <div class="d-grid">
          <button class="btn btn-outline-primary" @click="goToPlans">
            {{ $t('addons.paywall.upgrade_plan') }}
          </button>
        </div>
      </template>
    </template>
  </BModal>
</template>

<style scoped>
.paywall-qty {
  width: 108px;
  flex: 0 0 auto;
}
.paywall-qty .form-control {
  padding-left: 0.25rem;
  padding-right: 0.25rem;
}
</style>
